import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import tokens from '../tokens/tokens.json';

interface TokensInput {
  colors?: Record<string, string>;
  fonts?: { body?: string };
  fontSizes?: Record<string, string>;
  spacing?: Record<string, string>;
  radii?: Record<string, string>;
}

// buildMuiTheme creates an MUI theme from tokens.json
export function buildMuiTheme(tokensInput: TokensInput = tokens): Theme {
  const t = tokensInput ?? {};

  const palette = {
    primary: {
      main: t.colors?.['brand-primary'] ?? 'var(--color-brand-primary)',
    },
    secondary: {
      main: t.colors?.['brand-secondary'] ?? 'var(--color-brand-secondary)',
    },
    background: {
      default: t.colors?.['background'] ?? 'var(--color-background)',
      paper: t.colors?.['surface'] ?? 'var(--color-surface)',
    },
    text: {
      primary: t.colors?.['text-primary'] ?? 'var(--color-text-primary)',
      secondary: t.colors?.['text-secondary'] ?? 'var(--color-text-secondary)',
    },
    error: {
      main: t.colors?.['error'] ?? 'var(--color-error)',
    },
    success: {
      main: t.colors?.['success'] ?? 'var(--color-success)',
      light: t.colors?.['success-light'] ?? 'var(--color-success-light)',
    },
    info: {
      main: t.colors?.['info'] ?? 'var(--color-info)',
      light: t.colors?.['info-light'] ?? 'var(--color-info-light)',
    },
  };

  const typography = {
    fontFamily: t.fonts?.body ?? 'Inter, Roboto, system-ui, -apple-system, "Segoe UI", Arial',
    fontSize: t.fontSizes?.md ? parseInt(t.fontSizes.md, 10) : 16,
  };

  const spacingBase = t.spacing?.['2'] ? parseInt(t.spacing['2'].replace('px', ''), 10) : 8;

  const baseRadius = t.radii?.md ? parseInt(t.radii.md.replace('px', ''), 10) : 8;

  return createTheme({
    palette,
    typography,
    spacing: (factor: number) => spacingBase * factor,
    shape: {
      borderRadius: baseRadius,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: baseRadius,
            padding: '8px 16px',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          margin: 'none' as const,
          variant: 'outlined' as const,
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: baseRadius,
            // Idle border — doubled class for extra specificity so the
            // override wins even when MUI default styles are injected
            // after ours (happens with lazy-loaded date-picker TextField).
            '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: t.colors?.['border'] ?? 'var(--color-border)',
            },
            // Hover border — MUI defaults to rgba(0,0,0,0.87) on hover;
            // keep it slightly darker than idle but still on-brand.
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: t.colors?.['text-secondary'] ?? 'var(--color-text-secondary)',
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          variant: 'outlined' as const,
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
        },
      },
    },
  });
}

export default buildMuiTheme();
